import { useCallback, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import 'moment-timezone';
import { getTimelineTimedatas, type EventTimeline } from '../../core/Timelines';
import type { PortCallData } from '../../core/Core';
import type {
  PortCallBunkerOverride,
  PortCallEntry,
} from '@seaber/common/core/entries';
import { setIntersection } from '../../../lib/util';
import { getTimelinePortCalls } from '../shipment';
import type { ScenarioLocation } from '../../../lib/planningData';
import type { ClientId } from '../../core/ClientId';
import {
  hasFeature,
  useCurrencyRates,
  useDefaultCurrency,
} from '../../../state/zustand';
import type { PnlData } from '../../hooks/usePnlData';
import type { DefaultMap } from '@seaber/common/lib/collections';
import { getCostSharingDetailsForPortCalls } from '../TCE';
import type { PortCallCostSources } from './portCost';
import { getPortCosts } from './portCost';
import {
  getExternalPortCostPredictions,
  type PortCostPredictionByPortCall,
} from './portCostPrediction';
import { z } from 'zod';
import CalculationContext from '../../contexts/CalculationContext';
import { useMarcuraPortCosts } from '../../marcura/hooks';
import type { ChannelCost } from '../../pnl/channelCost/channelCost';

type CostParamSource =
  | 'SEABER'
  | 'EXTERNAL'
  | 'MANUAL'
  | 'cnbc'
  | 'NAPA'
  | 'INTEGRATION';

export const DEFAULT_PORT_COST_PRIORITY = {
  portCostOverride: 1,
  portCostDefault: 2,
  portCostIntegration: 3,
  portCostHistory: 4,
  portCostHistoryLatest: 5,
};

export const portCostPrioritySchema = z.object({
  portCostOverride: z.number().optional(),
  portCostIntegration: z.number().optional(),
  portCostDefault: z.number().optional(),
  portCostHistory: z.number().optional(),
  portCostHistoryLatest: z.number().optional(),
});

export type PortCostPriority = z.infer<typeof portCostPrioritySchema>;

export type PortCostPriorityKey = keyof PortCostPriority;

export const portCostPriorityKeys = Object.keys(
  portCostPrioritySchema.shape,
) as readonly PortCostPriorityKey[];

export type CostParam = {
  name: string;
  value: string | number | null;
  source: CostParamSource;
  testId?: string;
};

export type BunkerGradeParams = Omit<PortCallBunkerOverride, 'bunker_grade_id'>;

export type ShipDaysBreakdown = {
  totalDays: number;
  portDays: number;
  seaDays: number;
  offhireDays: number;
  billableDays: number;
};

export interface VoyageCostsChannelCost {
  type: 'CHANNEL';
  name: string;
  value: number;
  locations: Partial<ScenarioLocation>[];
  numShipments: number;
  /** PnL channel cost data with options and selection state */
  pnlChannelCost: ChannelCost;
}

export type Cost =
  | {
      type: 'BUNKER';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      portCallEntry: PortCallEntry | null;
      bunkerGradeParams?: BunkerGradeParams;
    }
  | {
      type: 'INVOICE_FREIGHT';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      bunkerGradeParams?: BunkerGradeParams;
    }
  | {
      type: 'PORT_CALL';
      name: string;
      value: number;
      params: CostParam[];
      locations: ScenarioLocation[];
      numShipments: number;
      options?: Partial<PortCallCostSources> & {
        portCallEntry: PortCallEntry;
        selected?:
          | 'default'
          | 'integration'
          | 'historical'
          | 'override'
          | 'historical-latest';
      };
      useActualPortCost?: boolean;
      isExternalPrediction?: boolean;
    }
  | {
      type: 'EXTRA_COST';
      name: string;
      value: number;
      locations: [];
      numShipments: number;
      params: CostParam[];
    }
  | {
      type: 'TC_HIRE';
      name: string;
      value: number;
      params: CostParam[];
      locations: [];
      numShipments: number;
      tcRatePerDay: number;
      shipDaysBreakdown: ShipDaysBreakdown;
    }
  | VoyageCostsChannelCost;

export type PortCallCost = Extract<Cost, { type: 'PORT_CALL' }>;
export type TcHireCost = Extract<Cost, { type: 'TC_HIRE' }>;

export type CostsByPortCall = Map<PortCallData['id'], Cost[]>;

type ShipmentsByPortCallId = DefaultMap<ClientId | null, Set<ClientId | null>>;

export const usePortCallCosts = (pnlData: PnlData) => {
  const apolloClient = useApolloClient();
  const currencyRates = useCurrencyRates();
  const isExternalPortCostPredictionsAvailable = hasFeature('s5_integration');
  const portCostPriority =
    useContext(CalculationContext)?.calculationConfig?.portCostPriority;
  const baseCurrency = useDefaultCurrency();
  const { portCosts: portCostMarcuraMaps } = useMarcuraPortCosts();

  const getTimelinePortCallCosts = useCallback(
    async (
      timeline: EventTimeline,
      shipmentsByPortCallId: ShipmentsByPortCallId,
      focusedShipments: Set<ClientId>,
    ): Promise<{
      errors: string[];
      portCallCosts: CostsByPortCall | null;
    } | null> => {
      if (timeline.type !== 'tc' || !timeline.vessel) {
        return null;
      }

      const costSharingDetailsByPortCall = getCostSharingDetailsForPortCalls(
        getTimelineTimedatas(timeline.parent ?? timeline),
      );

      const { locationById, portCalls } = getTimelinePortCalls(timeline);

      const errors: string[] = [];

      // Get external predictions if available
      let portCostPredictions: PortCostPredictionByPortCall | null = null;

      if (isExternalPortCostPredictionsAvailable) {
        const { portCostPredictionByPortCall, errors: fetchErrors } =
          await getExternalPortCostPredictions(
            portCalls,
            locationById,
            timeline.vessel,
            apolloClient,
            shipmentsByPortCallId,
          );

        if (fetchErrors.length > 0) {
          errors.push(...fetchErrors);
        }

        portCostPredictions = portCostPredictionByPortCall;
      }

      // Get port cost estimates first (includes default, history, integration)
      const portCallCosts = getPortCosts({
        currencyRates,
        baseCurrency,
        locationById,
        portCalls,
        portCostByVcMaps: pnlData.portCostByVcMaps,
        portCostEstimateMaps: pnlData.portCostEstimateMaps,
        portCostByCoaMaps: pnlData.portCostByCoaMaps,
        portCostMarcuraMaps,
        portCostPredictions,
        shipmentsByPortCallId,
        vessel: timeline.vessel,
        voyageGroups: timeline.voyageGroups,
      });

      for (const [, costs] of portCallCosts) {
        applyPrioritiesToPortCosts(costs, portCostPriority);
      }

      // Filter out costs for port calls that the focused shipments have no cargo operations at
      if (portCallCosts && focusedShipments.size > 0) {
        for (const portCallId of portCallCosts.keys()) {
          if (portCallId === null) continue;

          const costSharingDetails =
            costSharingDetailsByPortCall.get(portCallId);
          if (!costSharingDetails) continue;

          if (
            setIntersection([
              focusedShipments,
              costSharingDetails.shipmentsWithCargoOperations,
            ]).size < 1
          ) {
            portCallCosts.delete(portCallId);
          }
        }
      }

      return { portCallCosts, errors };
    },
    [
      apolloClient,
      currencyRates,
      isExternalPortCostPredictionsAvailable,
      pnlData.portCostByVcMaps,
      pnlData.portCostEstimateMaps,
      pnlData.portCostByCoaMaps,
      portCostPriority,
      baseCurrency,
      portCostMarcuraMaps,
    ],
  );

  return {
    getTimelinePortCallCosts,
  };
};

export type CostSourceWithPriority = {
  // Neste API prediction is considered as integration port cost
  source:
    | 'override'
    | 'integration'
    | 'historical'
    | 'historical-latest'
    | 'default';
  defaultId: number | null;
  priority: number;
  value: number;
};

/**
 * Applies priority rules to determine which cost source to select for PORT_CALL costs
 */
function applyPrioritiesToPortCosts(
  costs: Cost[],
  portCostPriority: PortCostPriority = DEFAULT_PORT_COST_PRIORITY,
): void {
  for (const cost of costs) {
    if (cost.type !== 'PORT_CALL' || !cost.options) continue;
    const { options, numShipments } = cost;

    const selectedSource = getSelectedPortCostSource(
      options,
      portCostPriority,
      numShipments,
    );

    if (selectedSource) {
      options.selected = selectedSource.source;
      cost.value = selectedSource.value;

      // Reset selectedId if we're not using a default cost
      if (
        selectedSource.source !== 'default' &&
        selectedSource.source !== 'override'
      ) {
        options.selectedId = undefined;
      }
    }
  }
}

/** Selects effective port cost by defined priorities */
export function getSelectedPortCostSource(
  sources: Partial<PortCallCostSources>,
  portCostPriority: PortCostPriority,
  numShipments: number,
): CostSourceWithPriority | undefined {
  const hasOverride = sources.oneTimeOverrideCost != null;
  const hasIntegration = !!sources.integrationCost;
  const hasHistory = !!sources.historicalValue;
  const hasLatestHistory = !!sources.latestHistoricalValue;
  const hasDefault = !!sources.defaultValue;
  const hasPrediction = !!sources.predictionCost;

  const availableSources: CostSourceWithPriority[] = [];

  if (hasOverride) {
    availableSources.push({
      source: 'override',
      priority: sources.useOneTimeOverrideCost
        ? -1
        : portCostPriority.portCostOverride || +Infinity,
      value: sources.oneTimeOverrideCost! / numShipments,
      defaultId: null,
    });
  }

  if (hasIntegration || hasPrediction) {
    availableSources.push({
      source: 'integration',
      priority: sources.useIntegrationPortCost
        ? -1
        : portCostPriority.portCostIntegration || +Infinity,
      value:
        (sources.integrationCost?.value || sources.predictionCost?.value || 0) /
        numShipments,
      defaultId: null,
    });
  }

  if (hasHistory) {
    availableSources.push({
      source: 'historical',
      priority: sources.useHistoricalPortCost
        ? -1
        : portCostPriority.portCostHistory || +Infinity,
      value: sources.historicalValue!.cost / numShipments,
      defaultId: null,
    });
  }

  if (hasLatestHistory && sources.latestHistoricalValue?.cost) {
    availableSources.push({
      source: 'historical-latest',
      priority: sources.useLatestHistoricalPortCost
        ? -1
        : portCostPriority.portCostHistoryLatest || +Infinity,
      value: sources.latestHistoricalValue.cost / numShipments,
      defaultId: null,
    });
  }

  if (hasDefault) {
    const selectedDefault =
      (sources.selectedId ? sources.defaultValue?.byId : undefined) ??
      sources.defaultValue?.byName ??
      sources.defaultValue?.byClass ??
      sources.defaultValue?.byTypes?.[0] ??
      null;

    const defaultValue = selectedDefault?.cost ?? 0;
    const defaultId = selectedDefault?.id ?? null;

    availableSources.push({
      source: 'default',
      priority: sources.portCostDefaultId
        ? -1
        : portCostPriority.portCostDefault || +Infinity,
      value: defaultValue,
      defaultId,
    });
  }

  // Sort by priority (lower numbers are higher priority)
  availableSources.sort((a, b) => a.priority - b.priority);
  return availableSources.at(0);
}
